import {
  discard,
  enqueue,
  failedCount,
  listQueued,
  markAttempted,
  markFailed,
  newIdempotencyKey,
  pendingCount,
  remove,
  retryFailed,
} from './queue.js'

/**
 * The API client, with the mutation queue underneath it.
 *
 * THE QUEUE IS ABOUT DEPLOYS, not about being offline.
 *
 * Kamal replaces the container on every deploy, and kamal-proxy answers 5xx for
 * the seconds that takes. Without the queue, an edit made in that window is
 * answered with an error and thrown away — silently, because a failed tick
 * looks exactly like a tick that never happened. The queue holds it and retries
 * once the new container is up.
 */

const BASE = import.meta.env.DEV ? '/api' : ''

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  constructor(code: string, message: string, status: number) {
    super(message)
    this.code = code
    this.status = status
  }
}

/** The request never reached the server — a dead upstream, or a deploy in progress. */
export class NetworkError extends Error {}

async function parseError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as
    | { error?: { code?: string; message?: string } }
    | null
  return new ApiError(
    body?.error?.code ?? 'internal',
    body?.error?.message ?? `HTTP ${response.status}`,
    response.status,
  )
}

export async function apiGet<T>(path: string): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, { credentials: 'same-origin' })
  } catch (cause) {
    throw new NetworkError(`could not reach the server: ${(cause as Error).message}`)
  }
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

// ---------------------------------------------------------------------------
// queue state, so a queued edit is never invisible
// ---------------------------------------------------------------------------

export interface QueueState {
  pending: number
  failed: number
  syncing: boolean
  /** The browser's own view of connectivity. Drives an immediate retry. */
  online: boolean
  lastSyncedAt: string | null
}

type Listener = (state: QueueState) => void

let state: QueueState = {
  pending: 0,
  failed: 0,
  syncing: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  lastSyncedAt: null,
}
const listeners = new Set<Listener>()

export function subscribeToQueue(listener: Listener): () => void {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

export function queueState(): QueueState {
  return state
}

function publish(patch: Partial<QueueState>): void {
  state = { ...state, ...patch }
  for (const listener of listeners) listener(state)
}

export async function refreshQueueState(): Promise<void> {
  publish({ pending: await pendingCount(), failed: await failedCount() })
}

// ---------------------------------------------------------------------------
// mutations
// ---------------------------------------------------------------------------

export interface MutationRequest {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  path: string
  body?: unknown
  /** Shown in the queue UI when something is waiting or has failed. */
  description: string
}

async function send(
  request: MutationRequest,
  idempotencyKey: string,
): Promise<{ ok: true; data: unknown } | { ok: false; error: ApiError } | { ok: false; offline: true }> {
  let response: Response
  try {
    response = await fetch(`${BASE}${request.path}`, {
      method: request.method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
    })
  } catch {
    return { ok: false, offline: true }
  }

  if (!response.ok) return { ok: false, error: await parseError(response) }
  return { ok: true, data: await response.json().catch(() => null) }
}

/**
 * Try now; queue if that fails.
 *
 * The key is minted here and handed to both the immediate attempt and the
 * queued copy, so a request that actually landed before the connection dropped
 * is recognised as a replay when the queue drains — the server returns the
 * original response instead of applying it twice.
 */
export async function apiMutate<T>(request: MutationRequest): Promise<T | null> {
  const idempotencyKey = newIdempotencyKey()

  if (state.online) {
    const result = await send(request, idempotencyKey)
    if (result.ok) {
      void refreshQueueState()
      return result.data as T
    }

    /*
     * A 4xx is the server having considered this request and rejected it.
     * Queueing it would just collect the same answer later, so it is thrown.
     *
     * A 5xx is NOT that. It means the request did not get processed, or we
     * cannot know whether it did — and crucially it is what a proxy returns
     * when the app behind it is down. In dev that is Vite; in production it is
     * kamal-proxy during the few seconds of a deploy. Treating it as a
     * rejection would silently discard an edit made while I was deploying.
     * It is queued instead, which the idempotency key makes safe.
     */
    if ('error' in result && result.error.status < 500) {
      throw result.error
    }
  }

  await enqueue({ ...request, body: request.body ?? null, idempotencyKey })
  await refreshQueueState()
  return null
}

/**
 * Drain the queue, oldest first, stopping at the first thing that looks like a
 * connection problem so the order is never broken.
 */
export async function replayQueue(): Promise<{ sent: number; failed: number; stopped: boolean }> {
  if (state.syncing) return { sent: 0, failed: 0, stopped: false }

  // Read the queue BEFORE announcing anything. A "syncing…" pill that flashes
  // every time a drain runs against an empty queue is a pill I learn to ignore,
  // and the whole point of it is that I notice it when it means something.
  const queued = await listQueued()
  if (queued.length === 0) return { sent: 0, failed: 0, stopped: false }

  publish({ syncing: true })

  let sent = 0
  let failed = 0
  let stopped = false

  try {
    for (const mutation of queued) {
      if (mutation.status !== 'pending' || mutation.id === undefined) continue

      const result = await send(
        { method: mutation.method, path: mutation.path, body: mutation.body, description: mutation.description },
        mutation.idempotencyKey,
      )

      if (result.ok) {
        await remove(mutation.id)
        sent += 1
        continue
      }

      if ('offline' in result) {
        // Still no signal. Leave everything queued, in order, and try later.
        await markAttempted(mutation.id, 'no connection')
        stopped = true
        break
      }

      if (result.error.status === 401) {
        // The session expired. Not this mutation's fault — keep it pending.
        await markAttempted(mutation.id, 'signed out')
        stopped = true
        break
      }

      if (result.error.status >= 500) {
        await markAttempted(mutation.id, result.error.message)
        stopped = true
        break
      }

      // A 4xx is the server rejecting this specific mutation. Retrying cannot
      // help, so it moves to `failed` and waits for me rather than blocking
      // everything behind it.
      await markFailed(mutation.id, `${result.error.code}: ${result.error.message}`)
      failed += 1
    }
  } finally {
    publish({ syncing: false, lastSyncedAt: new Date().toISOString() })
    await refreshQueueState()
  }

  return { sent, failed, stopped }
}

export { discard, listQueued, retryFailed }

/**
 * Keep trying until the queue is empty.
 *
 * The interval is the part that matters. During a deploy `navigator.onLine`
 * never changes — the network is fine, it is the server that is briefly gone —
 * so an 'online' event never fires and nothing would ever retry. Without this,
 * a mutation made mid-deploy would sit in IndexedDB until the tab happened to
 * be re-focused.
 */
export function startQueueWatcher(onDrained?: () => void, intervalMs = 15_000): () => void {
  const drain = () => {
    if (state.pending === 0 || state.syncing) return
    void replayQueue().then((result) => {
      if (result.sent > 0) onDrained?.()
    })
  }

  const goOnline = () => {
    publish({ online: true })
    drain()
  }
  const goOffline = () => publish({ online: false })

  window.addEventListener('online', goOnline)
  window.addEventListener('offline', goOffline)

  const timer = setInterval(drain, intervalMs)

  void refreshQueueState().then(drain)

  return () => {
    window.removeEventListener('online', goOnline)
    window.removeEventListener('offline', goOffline)
    clearInterval(timer)
  }
}

/** Test seam: force the client's view of connectivity. */
export function setOnlineForTesting(online: boolean): void {
  publish({ online })
}
